import { createFileRoute, Link } from "@tanstack/react-router";
import { SEOHead } from "@/components/shared/SEOHead";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
});

function PageNav() {
  return (
    <nav aria-label="Related pages" className="flex flex-wrap gap-2 mb-10">
      <Link
        to="/privacy"
        className="px-4 py-1.5 rounded-full text-sm font-medium bg-qds-accent text-qds-text-inverse transition-colors min-h-[44px] inline-flex items-center"
        aria-current="page"
      >
        Privacy Policy
      </Link>
      <Link
        to="/terms"
        className="px-4 py-1.5 rounded-full text-sm font-medium bg-qds-surface border border-qds-border text-qds-text-secondary hover:border-qds-accent/40 hover:text-qds-text-primary transition-colors min-h-[44px] inline-flex items-center"
      >
        Terms of Service
      </Link>
      <Link
        to="/support"
        className="px-4 py-1.5 rounded-full text-sm font-medium bg-qds-surface border border-qds-border text-qds-text-secondary hover:border-qds-accent/40 hover:text-qds-text-primary transition-colors min-h-[44px] inline-flex items-center"
      >
        Support
      </Link>
    </nav>
  );
}

function PrivacyPage() {
  return (
    <div className="page-enter bp-utility-page bp-legal-page">
      <SEOHead
        title="Privacy Policy"
        description="How Quuil collects, uses, and protects your information. The short version: we keep it minimal."
        path="/privacy"
      />

      <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <PageNav />

        <header className="mb-10">
          <h1 className="text-3xl md:text-4xl font-light tracking-tight text-qds-text-primary mb-3">
            Privacy Policy
          </h1>
          <p className="text-sm text-qds-text-tertiary">
            Last updated: January 7, 2026
          </p>
        </header>

        <div className="space-y-10 text-qds-text-secondary text-[15px] leading-relaxed">
          {/* Overview */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Overview</h2>
            <p>
              Your privacy matters to us. This policy explains what information Quuil
              ("we," "our," or "us") collects when you use our app and services, how
              we use it, and how we keep it safe. We try to collect only what we need
              and nothing more.
            </p>
            <p className="mt-3">
              By using Quuil, you agree to the collection and use of information described here.
              If you have questions, reach out — we're happy to explain anything in plain language.
            </p>
          </section>

          {/* Information We May Collect */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Information We May Collect</h2>
            <p className="mb-4">We may collect the following types of information:</p>

            <div className="space-y-4">
              <div className="bg-qds-surface rounded-xl p-5 border border-qds-border">
                <h3 className="text-sm font-medium text-qds-text-primary mb-1.5">Account Information</h3>
                <p className="text-sm">
                  When you create an account, we collect your display name and a unique identifier
                  provided by Apple's Sign in with Apple service. We do not collect your real name
                  or email address unless you choose to share them.
                </p>
              </div>

              <div className="bg-qds-surface rounded-xl p-5 border border-qds-border">
                <h3 className="text-sm font-medium text-qds-text-primary mb-1.5">Authentication Data</h3>
                <p className="text-sm">
                  We use Sign in with Apple for authentication. Apple provides us with a unique
                  user identifier and, if you choose, an email relay address. We store authentication
                  tokens securely to keep you signed in.
                </p>
              </div>

              <div className="bg-qds-surface rounded-xl p-5 border border-qds-border">
                <h3 className="text-sm font-medium text-qds-text-primary mb-1.5">Support Communications</h3>
                <p className="text-sm">
                  If you contact us for support, we may collect your email address and the content
                  of your communications to help resolve your issue.
                </p>
              </div>

              <div className="bg-qds-surface rounded-xl p-5 border border-qds-border">
                <h3 className="text-sm font-medium text-qds-text-primary mb-1.5">Purchases and Credits</h3>
                <p className="text-sm">
                  We track your credit balance and purchase history within the app. All payments
                  are processed by Apple through in-app purchases. We do not collect or store
                  your payment card information.
                </p>
              </div>

              <div className="bg-qds-surface rounded-xl p-5 border border-qds-border">
                <h3 className="text-sm font-medium text-qds-text-primary mb-1.5">Content You Provide</h3>
                <p className="text-sm">
                  This includes designs you create, images you upload, text prompts, and any content
                  you share to the Inkwell community gallery.
                </p>
              </div>

              <div className="bg-qds-surface rounded-xl p-5 border border-qds-border">
                <h3 className="text-sm font-medium text-qds-text-primary mb-1.5">Usage and Diagnostics</h3>
                <p className="text-sm">
                  We collect anonymous usage data and crash reports to improve app performance and
                  stability. This data does not personally identify you.
                </p>
              </div>

              <div className="bg-qds-surface rounded-xl p-5 border border-qds-border">
                <h3 className="text-sm font-medium text-qds-text-primary mb-1.5">Identifiers and Security</h3>
                <p className="text-sm">
                  We use device identifiers and Apple's App Attest service to verify the integrity
                  of requests and prevent fraud.
                </p>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">How We Use Information</h2>
            <ul className="list-disc pl-5 space-y-2 text-sm">
              <li>To provide, maintain, and improve the Service</li>
              <li>To process your transactions and manage your credit balance</li>
              <li>To authenticate your identity and secure your account</li>
              <li>To respond to your support requests</li>
              <li>To detect and prevent fraud or abuse</li>
              <li>To analyze usage patterns and improve user experience</li>
              <li>To send you important service-related communications</li>
            </ul>
          </section>

          {/* Photos and Uploads */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Photos and Uploads</h2>
            <p>
              When you upload photos or images, they are sent to our servers for processing (such
              as applying to products or generating designs). Uploaded images are stored temporarily
              for processing and are not shared with third parties except as needed to fulfill your
              orders (e.g., sending product images to our print fulfillment partner).
            </p>
          </section>

          {/* Analytics and Diagnostics */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Analytics and Diagnostics</h2>
            <p>
              We collect anonymized analytics data to understand how the app is used and to identify
              areas for improvement. Crash reports help us fix bugs and improve stability. This data
              is not linked to your identity.
            </p>
          </section>

          {/* Third-Party Services */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Third-Party Services</h2>
            <p className="mb-4">We work with trusted providers to operate core app features:</p>
            <div className="space-y-3">
              {[
                { name: "Apple account services", desc: "Secure sign-in and account setup" },
                { name: "Apple commerce services", desc: "In-app purchase processing for credits and subscriptions" },
                { name: "Cloud infrastructure providers", desc: "Reliable hosting, storage, and delivery of app services" },
                { name: "Creative workflow services", desc: "Design generation, product previews, catalog access, and order support" },
                { name: "Media processing services", desc: "Image preparation, refinement, and product mockup support" },
                { name: "Device integrity services", desc: "Security checks that help protect accounts, requests, and purchases" },
              ].map((service) => (
                <div key={service.name} className="flex gap-3 items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-qds-accent mt-2 shrink-0" aria-hidden="true" />
                  <div>
                    <span className="text-sm font-medium text-qds-text-primary">{service.name}</span>
                    <span className="text-sm text-qds-text-secondary"> — {service.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Data Retention & Deletion */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Data Retention &amp; Deletion</h2>
            <p>
              We retain your account data for as long as your account is active. You can request
              deletion of your account and associated data at any time through the app's settings
              or by contacting us. Upon deletion, we remove your personal data within 30 days,
              though some anonymized analytics data may be retained.
            </p>
          </section>

          {/* Your Choices */}
          <section>
            <h2 className="text-lg font-medium text-qds-text-primary mb-3">Your Choices</h2>
            <ul className="list-disc pl-5 space-y-2 text-sm">
              <li>You can delete your account and data through the app's settings</li>
              <li>You can choose to hide your email address using Apple's email relay</li>
              <li>You can control whether your designs are shared publicly on Inkwell</li>
              <li>You can contact us to request a copy of your data</li>
            </ul>
          </section>

          {/* Contact */}
          <section className="bg-qds-accent-tint rounded-xl p-6 border border-qds-accent/15">
            <h2 className="text-lg font-medium text-qds-text-primary mb-2">Questions?</h2>
            <p className="text-sm">
              If anything here is unclear, drop us a line at{" "}
              <a
                href="mailto:support@quuil.com"
                className="text-qds-accent hover:text-qds-accent-hover font-medium underline underline-offset-2 min-h-auto"
              >
                support@quuil.com
              </a>
              . We read every email.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
